import React, { useState } from "react";

import { Download } from "@mui/icons-material";
import {
  Typography,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import Dropzone from "react-dropzone";
import * as XLSX from "xlsx";

import CsvIcon from "../../../../assets/icons/csv-icon.svg?react";
import PdfIcon from "../../../../assets/icons/pdf-icon.svg?react";
import SampleCsvImg from "../../../../assets/images/sample-csv.webp";
import { PlanDropDown, Switch } from "../../../../common";
import CSV from "./CSV";
import PDF from "./PDF";
import { connect } from "react-redux";
import {
  uploadCsvInBulkForJob as uploadCsvInBulkForJobAction,
  uploadPdfsInBulkForJob as uploadPdfsInBulkForJobAction,
} from "../../../../redux/slices/resumeSlice";

const InviteInBulk = ({
  post,
  uploadPdfsInBulkForJob,
  uploadCsvInBulkForJob,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [activeResource, setActiveResource] = useState(false);
  const [expiresIn, setExpiredIn] = useState("1 week");
  const [files, setFiles] = useState([]);
  const [CsvTableData, setCsvTableData] = useState();
  const [uploadedFileType, setUploadedFileType] = useState(null);

  const templateData = [
    {
      Name: "John Smith",
      Email: "john@example.com",
      CSV: "john-resume.csv",
    },
    {
      Name: "Billy Joe",
      Email: "billy@example.com",
      CSV: "billy-resume.csv",
    },
  ];

  const handleCsvDownload = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        "Name,Email,CSV",
        ...templateData.map(
          (row) => `${row["Name"]},${row["Email"]},${row["CSV"]}`
        ),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "template.csv";
    link.click();
  };

  const handleXlsxDownload = () => {
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    XLSX.writeFile(workbook, "template.xlsx");
  };

  const validateFiles = (acceptedFiles, allowedExtensions) => {
    return acceptedFiles?.filter((file) =>
      allowedExtensions.includes(file.type)
    );
  };

  const onPdfDrop = (acceptedFiles, errors) => {
    if (files?.length === 100)
      return enqueueSnackbar("Only 100 pdfs allowed for now", {
        variant: "warning",
      });

    const validFiles = validateFiles(acceptedFiles, ["application/pdf"]);
    if (validFiles.length > 0) {
      setUploadedFileType("PDF");

      const newFilesState = [...files];

      for (let i = 0; i < acceptedFiles?.length; i++) {
        newFilesState.push({
          file: acceptedFiles[i],
          status: "In-progress",
        });
      }

      setFiles(newFilesState);

      // Start Upload API CALL
      setTimeout(() => {
        setFiles(newFilesState.map((f) => ({ ...f, status: "Complete" })));
      }, 5000);

      console.log(`Accepted PDF files`, newFilesState);
    } else {
      if (errors?.length > 0) {
        errors?.map((e) =>
          enqueueSnackbar(e?.errors[0]?.message, { variant: "warning" })
        );
      } else {
        enqueueSnackbar("Invalid file format", { variant: "warning" });
      }
    }
  };

  const onCsvDrop = (acceptedFiles, data) => {
    const validFiles = acceptedFiles.filter((file) => {
      const fileExtension = file.name.toLowerCase().split(".").pop();
      return ["csv", "xls", "xlsx"].includes(fileExtension);
    });

    if (validFiles.length > 0) {
      const file = validFiles[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const headers = parsedData[0];
        const rows = parsedData.slice(1).map((row) => {
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = row[index];
          });
          return rowData;
        });

        const parseFile = {
          data: rows,
          errors: [], // No errors for XLSX files
          meta: {
            aborted: false,
            cursor: file.size, // You may adjust this value according to your needs
            delimiter: ",", // Assuming the delimiter is comma
            fields: headers, // Assuming headers are consistent
            linebreak: "\n", // Assuming linebreak is newline
            truncated: false, // Assuming the file is not truncated
          },
        };

        console.log("LeadsImportStep1::> parseFile,", parseFile);
        setFiles(file);
        setUploadedFileType("CSV");
        setCsvTableData({
          data: parseFile.data,
          columns: makeColumns(parseFile.meta.fields),
        });
      };

      reader.readAsArrayBuffer(file);
    } else {
      if (data[0]?.errors[0]?.code === "file-too-large") {
        return enqueueSnackbar(data[0]?.errors[0]?.message, {
          autoHideDuration: 3000,
          variant: "warning",
        });
      }
      enqueueSnackbar(`File type not supported`, {
        autoHideDuration: 3000,
        variant: "warning",
      });
    }
  };

  const makeColumns = (rawColumns) => {
    // Filter out empty and unwanted headers
    const filteredColumns = rawColumns?.filter(
      (column) => column && !column.startsWith("_")
    );

    return filteredColumns?.map((column) => {
      return { Header: column, accessor: column };
    });
  };

  const uploadFiles = async (type) => {
    if (!files || files.length === 0) {
      enqueueSnackbar(`No ${type.toUpperCase()} files selected`, {
        variant: "warning",
      });
      return;
    }

    console.log(`Enter in upload bulk ${type}`);
    setLoading(true);

    const formData = new FormData();

    const additionalData = {
      company_id: post?.company_id,
      recruiter_id: post?.recruiter_id,
      job_post_id: post?.id,
      ...(type === "csv" && { data_to_append: CsvTableData?.columns }),
    };

    formData.append("additional_data", JSON.stringify(additionalData));

    if (type === "csv") {
      // Validate appended values
      const isEmptyAppendedValue = CsvTableData?.columns?.some(
        (col) => !col.appendedValue || col.appendedValue.trim() === ""
      );

      if (isEmptyAppendedValue) {
        enqueueSnackbar(
          "Please fill in all appended values before uploading CSV.",
          {
            variant: "warning",
          }
        );
        setLoading(false);

        return;
      }

      formData.append("file", files);
    } else if (type === "pdf") {
      files.forEach((file) => {
        formData.append("files", file.file);
      });
    }

    // Display the key/value pairs
    for (var pair of formData.entries()) {
      console.log(pair[0] + ", " + pair[1]);
    }

    try {
      if (type === "pdf") {
        await uploadPdfsInBulkForJob({
          data: formData,
          cb: () => onClose(),
        });
      } else {
        await uploadCsvInBulkForJob({
          data: formData,
          cb: () => onClose(),
        });
      }
      setLoading(false);
    } catch (error) {
      enqueueSnackbar(`Failed to upload ${type.toUpperCase()}s`, {
        variant: "error",
      });
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <Box display="flex" borderRadius="10px" height="100%" gap={2}>
      <Box
        maxWidth="35%"
        bgcolor="#FAFAFA"
        flex={1}
        mx="auto"
        p={4}
        borderRadius="20px"
        gap={2}
      >
        <Box>
          <Typography fontSize="17px" fontWeight={500} color="#475660" mb={2}>
            Important Note
          </Typography>
          <Typography fontSize="14px" color="#475660">
            Please format your file with exactly three columns in this order:{" "}
            <span style={{ fontWeight: 700 }}>Name, Email, CSV.</span> Each file
            can include up to 1,000 candidates.
          </Typography>

          <Box component="img" src={SampleCsvImg} width="100%" my={2} />
          <Box display="flex" alignItems="center">
            <Typography fontSize="12px" color="#374550">
              You can download templates as well
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              flex={1}
              justifyContent="flex-end"
              gap={1}
            >
              <Button
                startIcon={<Download sx={{ width: "16px", height: "16px" }} />}
                variant="main"
                sx={{ px: 1.5 }}
                onClick={handleCsvDownload}
              >
                CSV
              </Button>
              <Button
                startIcon={<Download sx={{ width: "16px", height: "16px" }} />}
                variant="main"
                sx={{ px: 1.5, bgcolor: "purple.main" }}
                onClick={handleXlsxDownload}
              >
                XLSX
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        flex={2}
        pr={2}
        display="flex"
        flexDirection="column"
        minHeight="350px" // Set fixed height as needed
      >
        {/* Scrollable content */}
        <Box flex={1} overflow="auto">
          <Box>
            {files?.length === 0 ? (
              <>
                <Typography color="#606060" fontWeight={600}>
                  Upload your file
                </Typography>
                <Box
                  display="flex"
                  gap={2}
                  flexDirection={{ xs: "column", md: "row" }}
                  alignItems="center"
                  mb={3}
                >
                  {/* PDF Dropzone */}
                  <Dropzone
                    accept={[".pdf"]}
                    onDrop={onPdfDrop}
                    multiple
                    maxFiles={100}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <ButtonBase
                        {...getRootProps()}
                        sx={{
                          width: "100%",
                          height: "160px",
                          border: "3px dashed #4BC7E1",
                          flexDirection: "column",
                          gap: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "4px",
                          bgcolor: "#F7F8F9",
                          my: 2,
                        }}
                      >
                        <input {...getInputProps()} accept={".pdf"} multiple />
                        <PdfIcon />
                        <Typography
                          color="#151515"
                          fontSize="18px"
                          fontWeight={500}
                        >
                          Upload PDFs
                        </Typography>
                        <Typography color="#999" fontSize="14px">
                          Max Files (100)
                        </Typography>
                      </ButtonBase>
                    )}
                  </Dropzone>

                  {/* CSV Dropzone */}
                  <Dropzone
                    accept={{
                      "text/csv": [".csv"],
                      "application/vnd.ms-excel": [".xls"],
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                        [".xlsx"],
                    }}
                    onDrop={onCsvDrop}
                    maxSize={5000000}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <ButtonBase
                        {...getRootProps()}
                        sx={{
                          width: "100%",
                          height: "160px",
                          border: "3px dashed #885EFA",
                          flexDirection: "column",
                          gap: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "4px",
                          bgcolor: "#F7F8F9",
                          my: 2,
                        }}
                      >
                        <input {...getInputProps()} />
                        <CsvIcon />
                        <Typography
                          color="#151515"
                          fontSize="18px"
                          fontWeight={500}
                        >
                          Upload CSV/XLSX
                        </Typography>
                        <Typography color="#999" fontSize="14px">
                          One File at a time (size: 10MB)
                        </Typography>
                      </ButtonBase>
                    )}
                  </Dropzone>
                </Box>
              </>
            ) : (
              <>
                {uploadedFileType === "CSV" && (
                  <CSV
                    files={files}
                    setFiles={setFiles}
                    setTableData={setCsvTableData}
                    tableData={CsvTableData}
                  />
                )}
                {uploadedFileType === "PDF" && (
                  <PDF files={files} setFiles={setFiles} />
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Sticky Button */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          pt={2}
          borderTop="1px solid #eee"
          bgcolor="#fff"
        >
          <Button
            variant="main"
            sx={{ fontSize: "16px" }}
            disabled={
              files?.length === 0 ||
              (Array.isArray(files) &&
                files.some((f) => f.status === "In-progress"))
            }
            onClick={() => {
              console.log(uploadedFileType);
              if (uploadedFileType === "CSV") {
                uploadFiles("csv");
              } else {
                uploadFiles("pdf");
              }
            }}
          >
            {loading ? (
              <CircularProgress sx={{ color: "white" }} size={18} />
            ) : (
              "Start screening"
            )}{" "}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const mapDipatchToProps = {
  uploadPdfsInBulkForJob: uploadPdfsInBulkForJobAction,
  uploadCsvInBulkForJob: uploadCsvInBulkForJobAction,
};

export default connect(null, mapDipatchToProps)(InviteInBulk);
